import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FolderType } from '../../../proto/utils/conversions';
import styles from '../Page.module.scss';
import folderStyles from './Folder.module.scss';
import MainWrapper from '../Utils/MainWrapper';
import ClickableItem from '../Utils/ClickableItem';
import defaultFavicon from '../img/favicon_default.png';

const isChildOf = (path, root) => {
  const first = path.shift();
  if (first && first.id === root.id) {
    for (let i = 0; i < path.length; i += 1) {
      root = root.folders.find((f) => f.id === path[i].id);
      if (!root) {
        return false;
      }
    }
    return true;
  }
  return false;
};

function FolderItem({ folder, onClick }) {
  const imgClasses = [styles.favicon];
  switch (folder.folder_type) {
    case FolderType.USER_FOLDER:
      imgClasses.push(folderStyles.user_folder);
      break;
    case FolderType.BOOKMARK_BAR:
      imgClasses.push(folderStyles.bookmarks_bar);
      break;
    case FolderType.USER_BOOKMARKS:
      imgClasses.push(folderStyles.user_bookmarks);
      break;
    case FolderType.TRASH:
      imgClasses.push(folderStyles.trash);
      break;
    case FolderType.UNSORTED_BOOKMARKS:
      imgClasses.push(folderStyles.unsorted);
      break;
    default:
      break;
  }

  return (
    <ClickableItem onClick={onClick}>
      <div className={imgClasses.join(' ')} />
      <span className={styles.title}>{folder.name}</span>
      <span className={folderStyles.itemsCount}>{folder.count}</span>
    </ClickableItem>
  );
}

FolderItem.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string.isRequired,
    folder_type: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

function LinkItem({
  title, url, favicon,
}) {
  if (!favicon) {
    favicon = defaultFavicon;
  }
  return (
    <ClickableItem url={url}>
      <img className={styles.favicon} src={favicon} alt="" />
      <span className={styles.title}>{title}</span>
      <span className={styles.url}>{url}</span>
    </ClickableItem>
  );
}

LinkItem.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  favicon: PropTypes.string.isRequired,
};

function Folder({ folderTree, caption }) {
  const { t } = useTranslation();

  const initialFolder = {
    folders: [],
    parent: null,
    name: t(caption),
  };

  const [root, setRoot] = useState(initialFolder);
  const [items, setItems] = useState([]);
  const [path, setPath] = useState([]);

  const openFolder = async (folder) => {
    if (folder.parent === null) {
      folder.name = initialFolder.name;
    }

    let cur = folder.parent;
    const pathItems = [];
    while (cur) {
      pathItems.push(cur);
      cur = cur.parent;
    }
    pathItems.reverse();
    setPath(pathItems);

    setRoot(folder);
    if (folder.items) {
      setItems(await folder.items.toArray());
    } else {
      setItems([]);
    }
  };

  const breadcrumbClicked = (i) => {
    const folder = path[i];
    openFolder(folder);
  };

  useEffect(() => {
    let folderToOpen = initialFolder;
    if (folderTree) {
      if (isChildOf([...path, root], folderTree)) {
        folderToOpen = root;
      } else {
        folderToOpen = folderTree;
      }
    }
    openFolder(folderToOpen);
  }, [folderTree]);

  return (
    <MainWrapper
      caption={root.name}
      path={path.map((p) => p.name)}
      onClick={breadcrumbClicked}
    >
      <div className={styles.list}>
        {
          root.folders.map((f) => (
            <FolderItem
              key={f.id}
              folder={f}
              onClick={() => openFolder(f)}
            />
          ))
        }
        {
          items.map((i) => (
            <LinkItem
              key={i.id}
              title={i.title}
              url={i.url}
              favicon={i.favicon}
            />
          ))
        }
      </div>
    </MainWrapper>
  );
}

Folder.propTypes = {
  folderTree: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    items: PropTypes.instanceOf(Object),
    folders: PropTypes.instanceOf(Array),
    parent: PropTypes.instanceOf(Object),
  }),
  caption: PropTypes.string.isRequired,
};

Folder.defaultProps = {
  folderTree: undefined,
};

export default Folder;
